// Routes — pages publiques (connexion, liens à jeton, catalogues, portes d'entrée).
// Lot 7.6 (2026-10-17) : App.tsx éclaté par domaine, enfants de <Routes> recopiés
// tels quels (ordre et commentaires), aucune URL changée ; rendu en fragment dans
// l'ordre d'origine par App.tsx.
import { lazy, Suspense, useEffect, useState } from "react";
import { Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { EXTERNAL_LINKS } from "../lib/external-links";
import LoginPage from "../pages/LoginPage";
import SignupPage from "../pages/SignupPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import QuestionnaireRepondrePage from "../pages/QuestionnaireRepondrePage";
import ProtocoleMesureImpactPage from "../pages/ProtocoleMesureImpactPage";
import ImpactReponsePage from "../pages/ImpactReponsePage";
import SondagePublicPage from "../pages/SondagePublicPage";
import ConfirmationPresencePage from "../pages/ConfirmationPresencePage";
import EmargementPage from "../pages/EmargementPage";
import SeanceHubPage from "../pages/SeanceHubPage";
import RepondreDisponibilitePage from "../pages/RepondreDisponibilitePage";
import { LoadingFallback } from "./communs";
import { segmentOrg, urlMonEspace, urlOrg } from "../lib/urls";

// Lot 9.0 (2026-10-18) : les anciennes URL de ce domaine (/plateforme,
// /plateforme/:id, /plateforme/groupes/:id, /plateforme/mon-statut…) sont
// redirigées par LA table routes/redirections.ts, rendue dans historiques.tsx —
// plus aucun <Navigate> ni composant de redirection écrit à la main ici.
// Porte d'entrée UNIQUE par groupe / séance (réconciliation ③.2 T1, 2026-08-17 ;
// lot 9.1, 2026-10-18). Les liens transverses (notifications, emails, cockpit,
// `lienGroupe` sans org connue) ne connaissent que l'id : le résolveur charge
// le groupe, trouve son ORG (segment /o/:slug) et son MODE, et renvoie à la
// seule page qui le sert — la garde de route bascule le jeton si besoin.
//   - participant pur → sa vue (/mon-espace/groupes/:id) ;
//   - groupe lisible dans l'org du jeton (GET /groupes/:id) → maison Inter
//     (/o/:slug/groupes/:id) ou Intra (/o/:slug/projets/:p[/produits/:pp]/groupes/:id) ;
//   - sinon groupe Inter accessible par appartenance (GET /plateforme/groupes/:id) ;
//   - sinon la liste des groupes animés.
// `?onglet=echanges|emargements` (posé par lienGroupe) devient `?tab=` en
// Inter, un segment de chemin en Intra ; le reste de la query est conservé.
interface GroupeResolu {
  mode?: string | null;
  organisationId?: string | null;
  organisationSlug?: string | null;
  projetId?: string | null;
  projetProduitId?: string | null;
}
export function GroupeHomeResolver() {
  const { groupeId, seanceId } = useParams();
  const { search } = useLocation();
  const { user } = useAuth();
  const [target, setTarget] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const roles: string[] = user?.roles ?? [];
      const encadre = roles.some((r) => ["SUPER_ADMIN", "PILOTE", "ANIMATEUR"].includes(r));
      if (!encadre) {
        setTarget(urlMonEspace(`/groupes/${groupeId}${search}`));
        return;
      }
      const params = new URLSearchParams(search);
      const onglet = params.get("onglet");
      params.delete("onglet");
      const reste = params.toString() ? `?${params.toString()}` : "";
      let g: GroupeResolu | null = null;
      try {
        g = (await api.get(`/groupes/${groupeId}`)).data.groupe ?? null;
      } catch {
        g = null;
      }
      if (!g) {
        try {
          g = { ...(await api.get(`/plateforme/groupes/${groupeId}`)).data.groupe, mode: "INTER" };
        } catch {
          g = null;
        }
      }
      if (!alive) return;
      if (!g) {
        setTarget(urlMonEspace("/animation/groupes"));
        return;
      }
      const seg = segmentOrg(g.organisationSlug, g.organisationId);
      const inter = g.mode === "INTER";
      if (seanceId) {
        // Lot 9.3 : LA page Séance, une adresse pour les deux modes.
        setTarget(urlOrg(`/groupes/${groupeId}/seances/${seanceId}${reste}`, seg));
        return;
      }
      // Lot 9.2c : UNE page groupe pour les deux modes — l'onglet en ?tab= (la
      // feuille de présence : sous-vue Émargements en Intra, onglet Séances en Inter).
      const tab = onglet === "echanges" ? "fil" : onglet === "emargements" ? (inter ? "seances" : "emargements") : null;
      const query = tab ? `${reste ? `${reste}&` : "?"}tab=${tab}` : reste;
      setTarget(urlOrg(`/groupes/${groupeId}${query}`, seg));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupeId, seanceId]);
  if (!target) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  return <Navigate to={target} replace />;
}
// Redirection serveur vers une URL externe (cecodev.fr) — utilisée pour les
// anciennes routes /cgv et /reglement-interieur héritées d'avant le
// regroupement des documents légaux sur le site vitrine.
function ExternalRedirect({ href }: { href: string }) {
  if (typeof window !== "undefined") {
    window.location.replace(href);
  }
  return null;
}
export const EpreuvePubliquePage = lazy(() => import("../pages/EpreuvePubliquePage"));
export const MesEmailsPage = lazy(() => import("../pages/MesEmailsPage"));
export const AnimateurRevenusPage = lazy(() => import("../pages/AnimateurRevenusPage"));
export const CatalogueCodevPage = lazy(() => import("../pages/CatalogueCodevPage"));
export const CatalogueFormationPage = lazy(() => import("../pages/CatalogueFormationPage"));
export const CatalogueDemarchesPage = lazy(() => import("../pages/CatalogueDemarchesPage"));
export const CatalogueFormationDetailPage = lazy(() => import("../pages/CatalogueFormationDetailPage"));
export const PlateformeCommentChoisirPage = lazy(() => import("../pages/PlateformeCommentChoisirPage"));
export const InteretReconfirmationPage = lazy(() => import("../pages/InteretReconfirmationPage"));
export const DevenirAnimateurInterPage = lazy(() => import("../pages/DevenirAnimateurInterPage"));
export const MesDocumentsAnimateurPage = lazy(() => import("../pages/MesDocumentsAnimateurPage"));
export const EspaceAnimateurDashboardPage = lazy(() => import("../pages/espace-animateur/DashboardPage"));
export const MesActionsPage = lazy(() => import("../pages/MesActionsPage"));
export const AdminReportingFinancierPage = lazy(() => import("../pages/AdminReportingFinancierPage"));
export const EspaceAnimateurMesGroupesPage = lazy(() => import("../pages/espace-animateur/MesGroupesPage"));
export const EspaceAnimateurAdministrationPage = lazy(() => import("../pages/espace-animateur/AdministrationPage"));
export const MesComptesClientsPage = lazy(() => import("../pages/espace-animateur/MesComptesClientsPage"));
const EspaceAnimateurEntretienAnnuelPage = lazy(() => import("../pages/espace-animateur/EntretienAnnuelPage"));
const EspaceAnimateurContactsAValiderPage = lazy(() => import("../pages/espace-animateur/ContactsAValiderPage"));
const MesPropositionsPage = lazy(() => import("../pages/espace-animateur/MesPropositionsPage"));
// Lot K (2026-10-27) : vérification PUBLIQUE d'un document par le code imprimé (QR de l'attestation de supervision).
const VerificationDocumentPage = lazy(() => import("../pages/VerificationDocumentPage"));
export const GroupePage = lazy(() => import("../pages/groupe/GroupePage"));
export const PlateformeFormationDetailPage = lazy(() => import("../pages/PlateformeFormationDetailPage"));
export const PlateformeInscriptionPage = lazy(() => import("../pages/PlateformeInscriptionPage"));
export const PlateformeInscriptionRetourPage = lazy(() => import("../pages/PlateformeInscriptionRetourPage"));
export const PlateformeInscriptionAnnulePage = lazy(() => import("../pages/PlateformeInscriptionAnnulePage"));

export const PlateformeSupervisionPage = lazy(() => import("../pages/PlateformeSupervisionPage"));
export const MesInscriptionsPage = lazy(() => import("../pages/MesInscriptionsPage"));
export const InterConventionEmployeurPage = lazy(() => import("../pages/InterConventionEmployeurPage"));
export const ActualitesPage = lazy(() => import("../pages/ActualitesPage"));
export const ContactPage = lazy(() => import("../pages/ContactPage"));
export const DocumentLegalPage = lazy(() => import("../pages/DocumentLegalPage"));
export function routesPubliques() {
  return (
    <>
{/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Vivier d'intérêt (Phase 5) : reconfirmation 1-clic depuis l'email d'expiration */}
      <Route path="/interets/reconfirmation" element={<Suspense fallback={<LoadingFallback />}><InteretReconfirmationPage /></Suspense>} />
      <Route path="/questionnaire/repondre/:token" element={<QuestionnaireRepondrePage />} />
      <Route path="/protocole-mesure-impact" element={<ProtocoleMesureImpactPage />} />
      <Route path="/sondage/:token" element={<SondagePublicPage />} />
      <Route path="/confirmer-presence/:token" element={<ConfirmationPresencePage />} />
      <Route path="/emarger/:token" element={<EmargementPage />} />
      <Route path="/verifier" element={<Suspense fallback={<LoadingFallback />}><VerificationDocumentPage /></Suspense>} />
      <Route path="/verifier/:code" element={<Suspense fallback={<LoadingFallback />}><VerificationDocumentPage /></Suspense>} />
      {/* Hub « Ma séance » : cible du lien stable posé dans l'agenda iCal. */}
      <Route path="/seance/:token" element={<SeanceHubPage />} />
      <Route path="/disponibilites/repondre/:token" element={<RepondreDisponibilitePage />} />
      <Route path="/impact/repondre/:token" element={<ImpactReponsePage />} />
      <Route path="/epreuve/:token" element={<Suspense fallback={<LoadingFallback />}><EpreuvePubliquePage /></Suspense>} />
      {/* Catalogues publics (chantier « 2 catalogues ») */}
      <Route path="/catalogue/codev" element={<Suspense fallback={<LoadingFallback />}><CatalogueCodevPage /></Suspense>} />
      <Route path="/catalogue/formation" element={<Suspense fallback={<LoadingFallback />}><CatalogueFormationPage /></Suspense>} />
      <Route path="/catalogue/formation/:id" element={<Suspense fallback={<LoadingFallback />}><CatalogueFormationDetailPage /></Suspense>} />
      {/* Lot O.7 (0306) : page mère des démarches publiées et UNE page par
          démarche (/catalogue/:slug) ; /catalogue/formation reste servie en
          littéral (adresse historique des emails et documents). */}
      <Route path="/catalogue" element={<Suspense fallback={<LoadingFallback />}><CatalogueDemarchesPage /></Suspense>} />
      <Route path="/catalogue/:slug" element={<Suspense fallback={<LoadingFallback />}><CatalogueFormationPage /></Suspense>} />
      <Route path="/catalogue/:slug/:id" element={<Suspense fallback={<LoadingFallback />}><CatalogueFormationDetailPage /></Suspense>} />
      <Route path="/plateforme/comment-choisir" element={<Suspense fallback={<LoadingFallback />}><PlateformeCommentChoisirPage /></Suspense>} />
      {/* /plateforme/historique supprimée (N198) : page orpheline (zéro
          lien entrant), recouverte par « Mes inscriptions » et l'espace
          animateur. On la remettra si un besoin réel réapparaît. */}
      {/* LA page groupe (lot 9.2c, modèle §8.0) : Intra et Inter, onglets ?tab=
          selon mode et casquette — les anciennes adresses (/plateforme/groupes/:id,
          /espace-animateur/groupes/:id, /projets/:p/groupes/:g/…) sont des entrées
          de routes/redirections.ts. */}
      <Route path="/o/:slug/groupes/:id" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><GroupePage /></Suspense></ProtectedRoute>} />
      <Route path="/plateforme/formation/:id" element={<Suspense fallback={<LoadingFallback />}><PlateformeFormationDetailPage /></Suspense>} />
      {/* Lot P.2 (FX 2026-11-04, option B) : le tunnel est OUVERT aux visiteurs non
          connectés — l'orientation (compte existant → connexion, sinon compte créé
          et vérifié par code) se joue à l'étape Coordonnées, plus devant la porte. */}
      <Route path="/plateforme/inscription/:groupeId" element={<Suspense fallback={<LoadingFallback />}><PlateformeInscriptionPage /></Suspense>} />
      {/* Page de retour Payplug — fallback redirect 3DS plein écran */}
      <Route path="/inter/inscription/retour" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><PlateformeInscriptionRetourPage /></Suspense></ProtectedRoute>} />
      {/* Page d'annulation Payplug (= PAYPLUG_CANCEL_URL) */}
      <Route path="/inter/inscription/annule" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><PlateformeInscriptionAnnulePage /></Suspense></ProtectedRoute>} />
      <Route path="/inter/convention/:signatureId" element={<Suspense fallback={<LoadingFallback />}><InterConventionEmployeurPage /></Suspense>} />
      {/* Lot 9.3b : le suivi Inter est l'onglet « Suivi Inter » de /mon-espace/inscriptions
          (/mon-espace/inscriptions-inter redirige, routes/redirections.ts). */}
      <Route path="/mon-espace/inscriptions-supervisees" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><PlateformeSupervisionPage /></Suspense></ProtectedRoute>} />
      {/* Bloc 7.C — documents participant unifiés */}
      {/* Lot 9.3b — « Mes inscriptions » à onglets (?tab= inscriptions · inter · documents) ;
          /mon-espace/documents redirige vers l'onglet Documents. */}
      <Route path="/mon-espace/inscriptions" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><MesInscriptionsPage /></Suspense></ProtectedRoute>} />
      {/* Bloc 7.C — historique des emails du participant (B6) */}
      <Route path="/mon-espace/emails" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><MesEmailsPage /></Suspense></ProtectedRoute>} />
      <Route path="/mon-espace/devenir-animateur" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><DevenirAnimateurInterPage /></Suspense></ProtectedRoute>} />
      {/* Mes actions à faire — dashboard transverse Anim + Pilotes */}
      <Route path="/o/:slug" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><MesActionsPage /></Suspense></ProtectedRoute>} />
      {/* Reporting financier — Cecodev only (RBAC ADMIN.reporting_financier.view) */}
      <Route path="/o/:slug/reporting-financier" element={<ProtectedRoute requiredRoles={["SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><AdminReportingFinancierPage /></Suspense></ProtectedRoute>} />
      {/* Sprint 7 — Hub animateur unifié (Inter + Intra) */}
      <Route path="/mon-espace/animation" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><EspaceAnimateurDashboardPage /></Suspense></ProtectedRoute>} />
      <Route path="/mon-espace/animation/groupes" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><EspaceAnimateurMesGroupesPage /></Suspense></ProtectedRoute>} />
      <Route path="/mon-espace/animation/contacts-a-valider" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><EspaceAnimateurContactsAValiderPage /></Suspense></ProtectedRoute>} />
      {/* « Mes propositions » (lot 10.7, N235) — page à onglets : proposer un groupe
          (planifier le créneau attribué `?candidature=`, porte privée `?prive=1`),
          proposer un événement (éligibles), mes positionnements sur les axes
          (@systeme: file-attente-axes). /mon-espace/animation/proposer,
          /positionnements et /proposer-evenement sont des entrées de
          routes/redirections.ts. */}
      <Route path="/mon-espace/animation/propositions" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><MesPropositionsPage /></Suspense></ProtectedRoute>} />
      <Route path="/mon-espace/animation/dossier" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><EspaceAnimateurAdministrationPage /></Suspense></ProtectedRoute>} />
      <Route path="/mon-espace/animation/comptes-clients" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><MesComptesClientsPage /></Suspense></ProtectedRoute>} />
      <Route path="/mon-espace/animation/entretien-annuel" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><EspaceAnimateurEntretienAnnuelPage /></Suspense></ProtectedRoute>} />
      {/* Bloc 7.B — vault documents animateur canonique (lecture seule ; dépôt via le dossier Qualiopi) */}
      <Route path="/mon-espace/animation/documents" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]}><Suspense fallback={<LoadingFallback />}><MesDocumentsAnimateurPage /></Suspense></ProtectedRoute>} />
      {/* Bloc 7.B — revenus animateur dans le hub */}
      <Route path="/mon-espace/animation/revenus" element={<ProtectedRoute requiredRoles={["ANIMATEUR", "SUPER_ADMIN", "PILOTE"]} orgPlateformeOnly><Suspense fallback={<LoadingFallback />}><AnimateurRevenusPage /></Suspense></ProtectedRoute>} />
      <Route path="/actualites" element={<Suspense fallback={<LoadingFallback />}><ActualitesPage /></Suspense>} />
      <Route path="/contact" element={<Suspense fallback={<LoadingFallback />}><ContactPage /></Suspense>} />
      {/* Documents légaux : les CGV sont désormais servies PAR L'APP (/cgv,
          version active de documents_legaux — c'est le texte que la case du
          tunnel fait accepter, audit financier 2026-09-18), comme les
          conditions d'usage des organisations clientes (/conditions-usage,
          lot 8.5 — le texte que le pilote accepte au nom de son org). Le
          règlement intérieur, CGU, mentions légales restent sur le site vitrine. */}
      <Route path="/cgv" element={<Suspense fallback={<LoadingFallback />}><DocumentLegalPage type="CGV" /></Suspense>} />
      <Route path="/conditions-usage" element={<Suspense fallback={<LoadingFallback />}><DocumentLegalPage type="CONDITIONS_USAGE" /></Suspense>} />
      <Route path="/reglement-interieur" element={<ExternalRedirect href={EXTERNAL_LINKS.reglementInterieur} />} />
      {/* Portes d'entrée UNIQUES par groupe / séance (réconciliation ③.2 T1) :
          cibles des liens transverses (notifications, emails) — résolution
          de la « maison » par rôle, cf. GroupeHomeResolver. */}
      <Route
        path="/groupes/:groupeId"
        element={
          <ProtectedRoute>
            <GroupeHomeResolver />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groupes/:groupeId/seances/:seanceId"
        element={
          <ProtectedRoute>
            <GroupeHomeResolver />
          </ProtectedRoute>
        }
      />
    </>
  );
}
